<script lang="ts">
  import type { NavbarSubItems, NavItem } from "types/backend/models/admin/navbar.entity";
  import { screenSize, Views } from "$lib/store/detect-view";
  import { utils } from "$lib/utils";
  import MenuButton from "./menu-button.svelte";
  import { locale } from "$lib/services/i18n";

  // exports
  export let data: NavItem;
  export let menuOpen: boolean;
  export let closeMenu: (ev: Event) => void;

  // vars
  const { label, subItems } = data;
  const subMenu = subItems || ({ children: [] } as unknown as NavbarSubItems);
  const { subItemLayout } = subItems as unknown as NavbarSubItems;
  let closeDesktopMenu = false;
  let slideInMobile = false;

  // functions

  function slideFn(ev: Event) {
    if ($screenSize === Views.DESKTOP) {
      return;
    }

    slideInMobile = true;
  }

  function closeSliderFn(ev: Event) {
    ev.stopPropagation();
    if ($screenSize === Views.DESKTOP) {
      return;
    }

    slideInMobile = false;
  }

  function toggleDesktopSubmenu(toggler: boolean): void {
    if ($screenSize !== Views.DESKTOP) {
      return;
    }

    closeDesktopMenu = toggler;
  }

  function removeTicketString(str?: string) {
    return str ? str.replace(/tickets/gi, "").toLowerCase() : "";
  }

  function screenClasses(item: NavItem) {
    return [item.mobileShow ? "mobile" : "", item.tabletShow ? "tablet" : "", item.desktopShow ? "desktop" : ""].join(
      " "
    );
  }

  function onSelect(ev: Event) {
    closeMenu(ev);
    toggleDesktopSubmenu(true);
  }

  // reactive

  $: if (menuOpen) {
    closeDesktopMenu = false;
    slideInMobile = false;
  }

  $: hasIcons = subMenu.children.reduce((acc: boolean, item: any) => acc || !!item.icon, false);

  const multyCol = subItemLayout === "double-column" && $screenSize === Views.DESKTOP;

  function isDesktop(ss: Views) {
    if (ss === Views.MOBILE || ss === Views.TABLET) {
      return false;
    }
    return true;
  }

  function genUrl(locale: string, url?: string) {
    if (locale === "en") {
      return url || "/";
    }
    return `/${locale}${url}`;
  }
</script>

<li
  on:mouseenter={() => toggleDesktopSubmenu(false)}
  on:mousedown={() => toggleDesktopSubmenu(false)}
  role="menuitem"
  tabindex="0"
  class={"menu-item " + screenClasses(data)}
>
  <!-- go to submenu -->
  <div title={label} on:click={slideFn} on:keydown={slideFn} class="nav-item" role="menuitem" tabindex="0">
    <button on:click={slideFn} class="sf-600" title={utils.capitalizeText(label, ["FC"])}>
      {utils.capitalizeText(label, ["FC"])}
    </button>
    <i />
  </div>

  <div
    class="sub-menu"
    class:hide-desktop-menu={closeDesktopMenu}
    class:slide-in={slideInMobile}
    style={`min-width: ${multyCol ? "430px" : "unset"}`}
  >
    <!-- shape -->
    <!-- <DropdownBackground /> -->

    <!-- sub list -->
    <ul style={`grid-template-columns: ${multyCol && isDesktop($screenSize) ? "1fr 1fr" : "1fr"};`}>
      <li class="close-menu"><button on:click={closeMenu} /></li>
      <!-- <DropdownBackground /> -->
      {#if $screenSize !== Views.DESKTOP}
        <!-- button back to previos page -->
        <li class="nav-item back" on:click={closeSliderFn} on:keydown={closeSliderFn} role="menuitem" tabindex="0">
          <i class="rotate" />
          <button class="pf-700">{utils.capitalizeText(label, ["FC"])}</button>
          <div class="separator" />
        </li>
      {/if}
      {#each subMenu.children as item}
        {@const { label, subItems, url, icon, separator } = item}

        {#if subItems}
          <svelte:self data={item} {closeMenu} {menuOpen} />
        {:else if separator}
          <li class="separator"></li>
        {:else}
          <li
            on:click={onSelect}
            on:keydown={onSelect}
            role="menuitem"
            tabindex="0"
            class={"sub-item " + screenClasses(item)}
          >
            <div class:hasIcons>
              {#if icon?.path}
                <img class="image" src={icon?.path} alt="" />
              {:else if hasIcons}
                <div class="image"></div>
              {/if}
              <a title={label?.toLowerCase()} class="sf-600" href={genUrl($locale, url)}>
                {utils.capitalizeText(removeTicketString(label), ["FC", "F.C."])}
              </a>
            </div>
          </li>
        {/if}
      {/each}
    </ul>
    {#if subMenu.btnLabel}
      {@const {
        btnLabel: label,
        btnUrl: url,
        btnBackgroundColor: backgroundColor,
        btnColor: color,
        btnIcon: icon,
      } = subMenu}
      <MenuButton button={{ label, icon, url, backgroundColor, color }} on:click={onSelect} on:keydown={onSelect} />
    {/if}
  </div>
</li>

<style lang="scss">
  @use "@styles/breakpoint-mixins" as *;

  a,
  button,
  li,
  ul {
    margin: 0;
    padding: 0;
    color: #000;
  }

  li {
    margin-bottom: 1.125rem;
    @include desktop {
      margin-bottom: unset;
    }
  }

  .menu-item {
    display: none;
    list-style: none;
    padding: unset;
    @include desktop {
      // @TODO in future if we have more lvl need to introduce update this
      //style prevent to have more than 1 lvl in desktop lvl
      &:has(.sub-menu) & {
        display: none;
      }
    }

    &:hover {
      cursor: pointer;
    }

    button {
      // sublink
      background: unset;
      border: unset;
      text-align: left;
      color: #000;
      font-size: 1.25rem;

      @include desktop {
        font-size: 1.125rem;
        font-weight: 500;
      }
    }
    i {
      pointer-events: none;
    }

    .nav-item {
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: center;
      font-size: 1.25rem;
      background-color: transparent;
      border: unset;
      gap: 10px;
      justify-content: space-between;
      justify-items: start;

      @include desktop {
        justify-items: unset;
        padding: 0;
        display: flex;
      }

      div {
        text-overflow: ellipsis;
        background-color: transparent;
        border: unset;
        color: var(--tp-black);

        @include desktop {
          max-width: 100%;
        }
      }

      i {
        display: block;
        width: 13px;
        height: 27px;
        background: url("@styles/assets/right-chevron.svg") no-repeat center;

        @include desktop {
          // display: none;
          transform: rotate(90deg) scale(0.8) translateX(2px) translateY(6px);
        }

        &.rotate {
          transform: rotate(180deg);
        }
      }

      &.back {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        // margin: 0 0 0 -12px;
        position: relative;
        padding: 18px 0;
        margin-bottom: 23px;

        button {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: 1.25rem;
          font-family: var(--secondary-font);
          text-align: unset;
          max-width: 90vw;
        }
      }
    }

    .sub-menu {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      position: absolute;
      left: 210vw;
      top: 0px;
      right: 0;
      list-style: none;
      font-size: 14px;
      -webkit-transition: left 0.3s;
      /* For Safari 3.1 to 6.0 */
      text-align: left;
      transition: left 0.3s;
      background: #fff;

      min-height: 100%;
      max-height: 100%;
      overflow-y: auto;
      z-index: 1;
      justify-content: space-between;
      padding: 5.7rem 2.44rem 0rem;

      @include desktop {
        padding: 1.88rem;
        border-radius: 0.44rem;
        min-width: 300px;
        /* max-width: 300px; */
        max-height: 70vh;
        overflow-y: scroll;
        &::-webkit-scrollbar {
          display: none; /* Hide scroll bar */
        }
      }

      ul {
        display: grid;
        margin: 0;
        gap: 0 32px;

        @include desktop {
          gap: 0 53px;
        }
        .close-menu {
          position: absolute;
          top: 4.25rem;
          right: 3.1rem;
          width: unset;

          button {
            width: 1em;
            background: url("@styles/assets/close.svg") no-repeat;
            background-size: contain;
            height: 1em;
            border: none;
            transform: scale(1.2);
          }
          @include desktop {
            display: none;
          }
        }

        li {
          width: 100%;
          margin-bottom: 14px;

          @include desktop {
            max-width: 300px;
            padding: 0 0 5px 0;
            margin: 0;
          }

          &:hover a {
            @include desktop {
              color: var(--tp-pink);
              cursor: pointer;
            }
          }

          div {
            a {
              font-family: var(--secondary-font);
              font-weight: 600;
              line-height: 1.875rem;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              -webkit-line-clamp: 3;
              -webkit-box-orient: vertical;
              width: 100%;
              color: #000;
              font-size: 1.125rem;
              font-style: normal;
              @include desktop {
                width: 100%;
              }
            }

            .image {
              height: 1rem;
              width: 1rem;
            }
            @include desktop {
              display: flex;
              i {
                transform: rotate(0deg);
              }
            }
          }
        }
      }

      // Show - hide items

      &.slide-in {
        left: 0;
        -webkit-transition: left 0.3s;
        /* For Safari 3.1 to 6.0 */
        transition: left 0.3s;
      }

      @include desktop {
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        right: unset;
        width: max-content;
        display: none;
        box-shadow: var(--dropdown-box-shadow);
      }

      &.hide-desktop-menu {
        @include desktop {
          display: none !important;
        }
      }

      .sub-item {
        display: none;

        &.mobile {
          display: block;

          @include tablet {
            display: none;
          }
        }

        &.tablet {
          @include tablet {
            display: block;
          }

          @include desktop {
            display: none;
          }
        }

        &.desktop {
          @include desktop {
            display: block;
          }
        }

        > div {
          display: grid;
          grid-template-columns: 1fr;
          align-items: center;
          font-size: 1.25rem;

          &.hasIcons {
            grid-template-columns: auto 1fr;
            grid-gap: 1rem;
          }
        }
      }
      .separator {
        height: 1px;
        width: 100%;
        background: #010ebc;
        position: absolute;
        bottom: 0;

        // left: 1rem;
      }
    }

    &.mobile {
      display: block;

      @include tablet {
        display: none;
      }
    }

    &.tablet {
      @include tablet {
        display: block;
      }

      @include desktop {
        display: none;
        white-space: nowrap;
      }
    }

    &.desktop {
      @include desktop {
        display: block;
      }
    }

    a {
      text-decoration: none;
      cursor: pointer;
      font-size: 1.25rem;
    }

    @include desktop {
      // Ul  on hover open li list
      position: relative;

      &:hover > .sub-menu {
        display: block;
        position: absolute;
        // padding: 15px 0;

        &:after {
          content: "";
          color: #fff;
          position: absolute;
          top: -18px;
          left: 0;
          width: 100%;
          height: 40px;
        }
      }

      // hover effect display nav sub items 4 lvls deep
      .sub-menu .menu-item .sub-menu,
      .sub-menu .menu-item .sub-menu .menu-item .sub-menu,
      .sub-menu .menu-item .sub-menu .menu-item .sub-menu .menu-item .sub-menu {
        display: none;
      }

      .sub-menu .menu-item:hover .sub-menu,
      .sub-menu .menu-item .sub-menu .menu-item:hover .sub-menu,
      .sub-menu .menu-item .sub-menu .menu-item .sub-menu .menu-item:hover .sub-menu {
        top: 0%;
        left: 95%;
        display: block;
        transform: translateX(-10%);
      }
    }
  }
</style>
